import '../css/styles.css';
import { initializeFirebase, auth, db, getFieldValue } from './firebase-config.js';
import { CycleAuth, setupAuthObserver } from './auth.js';
import { HelperProfile } from './helpers.js';
import { BookingSystem } from './booking.js';
import { 
    loadStats, currentSlotId, currentHelperId, isAuthMode,
    scrollToTop, toggleMobileMenu, showHelpers, openAuthModal, closeAuthModal, openDashboard, closeDashboard,
    closeHelperModal, closeBookingModal, closeFeedbackModal, showTab, toggleAuthMode, togglePasswordVisibility,
    handleSeekerClick, handleHelperClick, showHelperDetail, bookSlot, filterHelpers,
    setRating, submitFeedback, showFeedbackModal, handleBookingResponse,
    openHelperApplication, closeHelperApplication,
    toggleIntroMethod, validateHelperApplication,
    saveDraft, resumeDraft, discardDraft,
    loadUserSessions, loadUserProfile, loadMySlots, updateCalendlyUrl,
    openCalBooking, closeCalModal, openCalUrlModal, closeCalUrlModal, handleCalUrlSubmit
} from './ui.js';
import { Validator, sanitizeContentSync, toast, FormValidation, Loading } from './utils.js';
import errorHandler from './error-handler.js';
import rateLimiter from './rate-limiter.js';
import { ApplicationSystem } from './applications.js';
import { setupEventDelegation } from './event-delegation.js';
import ariaLive from './aria-live.js';

// Setup global functions immediately (before DOMContentLoaded)
window.scrollToTop = scrollToTop;
window.toggleMobileMenu = toggleMobileMenu;
window.showHelpers = showHelpers;
window.openAuthModal = openAuthModal;
window.closeAuthModal = closeAuthModal;
window.openDashboard = openDashboard;
window.closeDashboard = closeDashboard;
window.closeHelperModal = closeHelperModal;
window.closeBookingModal = closeBookingModal;
window.closeFeedbackModal = closeFeedbackModal;
window.showTab = showTab;
window.toggleAuthMode = toggleAuthMode;
window.togglePasswordVisibility = togglePasswordVisibility;
window.handleSeekerClick = handleSeekerClick;
window.handleHelperClick = handleHelperClick;
window.showHelperDetail = showHelperDetail;
window.bookSlot = bookSlot;
window.filterHelpers = filterHelpers;
window.setRating = setRating;
window.submitFeedback = submitFeedback;
window.showFeedbackModal = showFeedbackModal;
window.handleBookingResponse = handleBookingResponse;
window.signOut = () => CycleAuth.signOut();
window.openHelperApplication = openHelperApplication;
window.closeHelperApplication = closeHelperApplication;
window.toggleIntroMethod = toggleIntroMethod;
window.validateHelperApplication = validateHelperApplication;
window.loadMySlots = loadMySlots;
window.saveDraft = saveDraft;
window.resumeDraft = resumeDraft;
window.discardDraft = discardDraft;
window.loadUserSessions = loadUserSessions;
window.updateCalendlyUrl = updateCalendlyUrl;
window.openCalBooking = openCalBooking;
window.closeCalModal = closeCalModal;
window.openCalUrlModal = openCalUrlModal;
window.closeCalUrlModal = closeCalUrlModal;
window.handleCalUrlSubmit = handleCalUrlSubmit;
window.CycleAuth = CycleAuth;
window.ariaLive = ariaLive;
window.resendVerificationEmail = async () => {
    try {
        const result = await CycleAuth.resendVerificationEmail();
        if (result.success) {
            toast.success('Verification email sent! Please check your inbox.');
        } else {
            toast.error(result.error);
        }
    } catch (error) {
        toast.error('Error sending verification email. Please try again.');
    }
};

window.dismissEmailBanner = () => {
    const banner = document.getElementById('emailVerificationBanner');
    if (banner) {
        banner.style.display = 'none';
        // Store dismissal in session storage
        sessionStorage.setItem('emailBannerDismissed', 'true');
        toast.info('Email verification reminder dismissed. You can verify your email anytime from your dashboard.');
    }
};

// Initialize the application
let firebaseInitialized = false;
document.addEventListener('DOMContentLoaded', function() {
    // Check if Firebase is loaded
    if (typeof firebase === 'undefined') {
        console.error('Firebase SDK not loaded. Please check your internet connection.');
        toast.error('Error loading Firebase. Please refresh the page.');
        return;
    }
    
    // Initialize Firebase only once
    if (!firebaseInitialized) {
        firebaseInitialized = true;
        if (!initializeFirebase()) {
            toast.error('Error initializing Firebase. Please refresh the page.');
            return;
        }
        // Mark app as ready
        document.body.setAttribute('data-app-ready', 'true');
        
        // Log build info
        if (typeof BUILD_TIMESTAMP !== 'undefined') {
            console.log(`Build: ${BUILD_TIMESTAMP} | Version: ${BUILD_VERSION || 'dev'}`);
        }
    }
    
    // Check for email link sign-in FIRST, then set up auth observer
    (async () => {
        try {
            const result = await CycleAuth.completeSignInWithEmailLink();
            if (result.success) {
                console.log('Successfully signed in via email link');
            }
        } catch (error) {
            // Silently ignore if not an email link
            console.log('Not an email link sign-in');
        }
        
        // Check for email verification action
        try {
            const urlParams = new URLSearchParams(window.location.search);
            const mode = urlParams.get('mode');
            const oobCode = urlParams.get('oobCode');
            
            if (mode === 'verifyEmail' && oobCode) {
                console.log('Processing email verification...');
                
                // Apply the verification code
                await auth.applyActionCode(oobCode);
                
                // Reload current user to get updated verification status
                if (auth.currentUser) {
                    await auth.currentUser.reload();
                }
                
                // Clean URL
                const cleanUrl = window.location.pathname;
                window.history.replaceState({}, document.title, cleanUrl);
                
                // Show success message
                toast.success('Email verified successfully! Welcome to Cycle of Help.');
                
                console.log('Email verification completed');
            }
        } catch (error) {
            console.error('Email verification error:', error);
            if (error.code === 'auth/invalid-action-code') {
                toast.error('This verification link has expired or already been used. Please request a new one.');
            } else {
                toast.error('Error verifying email. Please try again.');
            }
        }
        
        // Initialize UI and auth observer AFTER email link check completes
        initializeUI();
        setupAuthObserver();
    })();
    
    // Set up event listeners
    setupEventListeners();
    
    // Set up event delegation for all onclick handlers
    setupEventDelegation();
    
    // Set up intro method change listener
    const appIntroMethod = document.getElementById('appIntroMethod');
    if (appIntroMethod) {
        appIntroMethod.addEventListener('change', toggleIntroMethod);
    }
    
    // Initialize form validation
    FormValidation.init('authForm');
    FormValidation.init('bookingForm');
    FormValidation.init('profileForm');
    FormValidation.init('helperApplicationForm');
    
    // Add paste event listener for bio field to detect truncation
    const profileBioField = document.getElementById('profileBio');
    if (profileBioField) {
        profileBioField.addEventListener('paste', (e) => {
            // Get the pasted text
            const pastedText = (e.clipboardData || window.clipboardData).getData('text');
            const currentText = profileBioField.value;
            const selectionStart = profileBioField.selectionStart;
            const selectionEnd = profileBioField.selectionEnd;
            
            // Calculate what the new text would be after paste
            const newText = currentText.substring(0, selectionStart) + pastedText + currentText.substring(selectionEnd);
            
            // Check if it exceeds the maxlength
            const maxLength = profileBioField.maxLength;
            if (maxLength > 0 && newText.length > maxLength) {
                // Show toast notification after a small delay to allow browser truncation
                setTimeout(() => {
                    toast.warning(`Bio was truncated to ${maxLength} characters. Some text was removed.`);
                }, 100);
            }
        });
    }
    
    // Load initial data
    loadStats();
    
    
    // Check URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('view') === 'helpers') {
        window.showHelpers();
    }
});

function initializeUI() {
    // Initialize navigation with Sign In button
    const navLinks = document.getElementById('navLinks');
    if (!navLinks.querySelector('.auth-buttons')) {
        const div = document.createElement('div');
        div.className = 'auth-buttons';
        div.innerHTML = '<button class="cta-button" id="initialSignInBtn">Sign In</button>';
        navLinks.appendChild(div);
        
        // Add event listener
        document.getElementById('initialSignInBtn').addEventListener('click', () => {
            if (window.openAuthModal) {
                window.openAuthModal();
            } else {
                console.error('openAuthModal not yet available');
            }
        });
    }
}

function setupEventListeners() {
    // Add keyboard support for logo click
    const logo = document.querySelector('.logo');
    if (logo) {
        logo.addEventListener('keypress', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                scrollToTop();
            }
        });
    }
    
    // Dynamic password required attribute based on email
    const authEmail = document.getElementById('authEmail');
    const authPassword = document.getElementById('authPassword');
    
    authEmail.addEventListener('blur', () => {
        const email = authEmail.value;
        if (email && window.isAuthMode === 'signin' && CycleAuth.isAdminEmail(email)) {
            // Admin email - password not required
            authPassword.removeAttribute('required');
        } else {
            // Non-admin or signup mode - password required
            authPassword.setAttribute('required', '');
        }
    });
    
    // Auth form handler
    document.getElementById('authForm').addEventListener('submit', async (e) => {
        e.preventDefault();
        
        const submitButton = e.target.querySelector('button[type="submit"]');
        const email = document.getElementById('authEmail').value;
        const password = document.getElementById('authPassword').value;
        
        // Validate inputs
        if (!Validator.email(email)) {
            toast.warning('Please enter a valid email address');
            return;
        }
        
        // Handle empty password for admin email sign-in
        if (password === '') {
            if (window.isAuthMode === 'signin' && CycleAuth.isAdminEmail(email)) {
                // Admin with empty password - send email link
                try {
                    Loading.button(submitButton, true);
                    const result = await CycleAuth.sendSignInLink(email);
                    if (result.success) {
                        toast.success('Sign-in link sent to your email! Check your inbox.');
                        window.closeAuthModal();
                        rateLimiter.reset('auth', email);
                    } else {
                        throw new Error(result.error);
                    }
                } catch (error) {
                    errorHandler.handleError(error);
                } finally {
                    Loading.button(submitButton, false);
                }
                return;
            } else {
                // Non-admin or signup mode with empty password - require password
                toast.warning('Password is required');
                return;
            }
        }
        
        if (!Validator.password(password)) {
            toast.warning('Password must be at least 8 characters long');
            return;
        }
        
        try {
            // Check rate limit
            rateLimiter.isAllowed('auth', email);
            
            // Show loading state
            Loading.button(submitButton, true);
            
            let result;
            if (window.isAuthMode === 'signup') {
                const fullName = document.getElementById('authName').value;
                
                if (!Validator.name(fullName)) {
                    Loading.button(submitButton, false);
                    throw new Error('Please enter your full name');
                }
                
                result = await CycleAuth.signUp(email, password, fullName);
                
                if (result.success && result.emailVerificationSent) {
                    toast.success('Account created successfully! Please check your email to verify your account before you can access all features.');
                }
            } else {
                result = await CycleAuth.signIn(email, password);
            }
            
            if (result.success) {
                window.closeAuthModal();
                rateLimiter.reset('auth', email);
            } else {
                throw new Error(result.error);
            }
        } catch (error) {
            errorHandler.handleError(error, 'AUTH_FORM');
        } finally {
            // Remove loading state
            Loading.button(submitButton, false);
        }
    });
    
    // Booking form handler
    document.getElementById('bookingForm').addEventListener('submit', async (e) => {
        e.preventDefault();
        
        const submitButton = e.target.querySelector('button[type="submit"]');
        
        // Check email verification - TEMPORARILY DISABLED
        // if (!window.isEmailVerified) {
        //     toast.warning('Please verify your email address before booking a session. Check your inbox for the verification email.');
        //     return;
        // }
        
        const messageField = document.getElementById('bookingMessage');
        const formatField = document.getElementById('bookingFormat');
        
        // Validate fields
        if (!Validator.textLength(messageField.value, 10, 500)) {
            toast.warning('Please describe what you need help with (10-500 characters)');
            return;
        }
        
        if (!formatField.value || !['video', 'phone', 'chat'].includes(formatField.value)) {
            toast.warning('Please select a communication format');
            return;
        }
        
        try {
            // Check rate limit
            rateLimiter.isAllowed('booking', window.currentUser.uid);
            
            // Show loading state
            Loading.button(submitButton, true);
            
            const result = await BookingSystem.requestSession(
                window.currentUser.uid,
                window.currentHelperId,
                window.currentSlotId,
                {
                    message: sanitizeContentSync(messageField.value.trim()),
                    format: formatField.value
                }
            );
            
            if (result.success) {
                toast.success('Session request sent! You\'ll receive an email when the helper responds.');
                window.closeBookingModal();
                // Clear form
                document.getElementById('bookingForm').reset();
            } else {
                throw new Error(result.error);
            }
        } catch (error) {
            errorHandler.handleError(error, 'BOOKING_FORM');
        } finally {
            Loading.button(submitButton, false);
        }
    });
    
    // Profile form handler
    document.getElementById('profileForm').addEventListener('submit', async (e) => {
        e.preventDefault();
        
        const submitButton = e.target.querySelector('button[type="submit"]');
        const nameField = document.getElementById('profileName');
        const bioField = document.getElementById('profileBio');
        const expertiseField = document.getElementById('profileExpertise');
        
        // Validate fields
        if (!Validator.name(nameField.value)) {
            toast.warning('Name must be between 2 and 100 characters');
            return;
        }
        
        if (bioField.value && !Validator.textLength(bioField.value, 0, 1000)) {
            toast.warning('Bio must not exceed 1000 characters');
            return;
        }
        
        // Get selected expertise areas
        const selectedExpertise = Array.from(expertiseField.checked).map(checkbox => checkbox.value);
        const validExpertise = ['career', 'finance', 'legal', 'health', 'mental-health', 'education', 'tech', 'business'];
        
        // Validate all selected expertise areas
        if (selectedExpertise.length > 0) {
            const invalidExpertise = selectedExpertise.filter(exp => !validExpertise.includes(exp));
            if (invalidExpertise.length > 0) {
                toast.warning('Please select valid expertise areas');
                return;
            }
        }
        
        try {
            // Check rate limit
            rateLimiter.isAllowed('profile', window.currentUser.uid);
            
            // Show loading state
            Loading.button(submitButton, true);
            
            // Update user name
            await db.collection('users').doc(window.currentUser.uid).update({
                fullName: sanitizeContentSync(nameField.value.trim())
            });
            
            // Check if user already has a helper profile
            const helperDoc = await db.collection('helpers').doc(window.currentUser.uid).get();
            const hasHelperProfile = helperDoc.exists;
            
            // Build helper profile data - sanitize first, then apply || null convention
            const sanitizedBio = sanitizeContentSync(bioField.value.trim());
            const helperProfileData = {
                bio: sanitizedBio || null
            };
            
            // Handle expertise fields - explicitly set empty arrays to clear them
            if (selectedExpertise.length > 0) {
                helperProfileData.expertiseAreas = selectedExpertise;
                helperProfileData.primaryExpertise = selectedExpertise[0]; // Keep first one for backwards compatibility
            } else {
                // Clear expertise fields when none selected
                helperProfileData.expertiseAreas = [];
                helperProfileData.primaryExpertise = getFieldValue().delete();
            }
            
            // Only create/update helper profile if there's meaningful data or profile already exists
            const hasMeaningfulData = bioField.value.trim() || selectedExpertise.length > 0;
            if (hasHelperProfile || hasMeaningfulData) {
                await HelperProfile.createOrUpdateHelper(window.currentUser.uid, helperProfileData);
            }
            
            toast.success('Profile updated successfully!');
        } catch (error) {
            errorHandler.handleError(error, 'PROFILE_UPDATE');
        } finally {
            Loading.button(submitButton, false);
        }
    });
    
    // Availability form handler removed - now using Cal.com integration
    
    // Helper Application form handler
    document.getElementById('helperApplicationForm').addEventListener('submit', async (e) => {
        e.preventDefault();
        //console.log('init sumbmit');
        // Check email verification - TEMPORARILY DISABLED
        // if (!window.isEmailVerified) {
        //     toast.warning('Please verify your email address before submitting an application. Check your inbox for the verification email.');
        //     return;
        // }
        
        // Validate form for security
        if (!validateHelperApplication()) {
            return;
        }
        
        try {
            // Collect all form data
            const applicationData = {
                userId: window.currentUser ? window.currentUser.uid : null,
                basicInfo: {
                    fullName: document.getElementById('appFullName') ? document.getElementById('appFullName').value : null,
                    email: document.getElementById('appEmail') ? document.getElementById('appEmail').value : null,
                    phone: document.getElementById('appPhone') ? document.getElementById('appPhone').value : null,
                    title: document.getElementById('appTitle') ? document.getElementById('appTitle').value : null,
                    experience: document.getElementById('appExperience') ? document.getElementById('appExperience').value : null,
                    linkedIn: document.getElementById('appLinkedIn') ? document.getElementById('appLinkedIn').value : null
                },
                expertise: {
                    areas: document.getElementsByClassName('appExpertise_class') ? 
                        Array.from(document.getElementsByClassName('appExpertise_class')).filter(checkbox => checkbox.checked).map(checkbox => checkbox.value) : [],
                    certifications: document.getElementById('appCertifications') ? 
                        document.getElementById('appCertifications').value : null
                },
                introduction: {
                    method: document.getElementById('appIntroMethod') ? document.getElementById('appIntroMethod').value : null,
                    writtenIntro: document.getElementById('appWrittenIntro') ? 
                        (document.getElementById('appWrittenIntro').value || null) : null,
                    calendlyUrl: document.getElementById('appCalendlyUrl') ? document.getElementById('appCalendlyUrl').value : null,
                    website: document.getElementById('appWebsite') ? document.getElementById('appWebsite').value : null
                }
            };
            
            // Collect files
            const files = {
                video: document.getElementById('appVideoUpload') ? 
                    document.getElementById('appVideoUpload').files[0] : undefined,
                photo: document.getElementById('appPhoto') ? 
                    document.getElementById('appPhoto').files[0] : undefined
            };
            
            // Remove undefined files
            Object.keys(files).forEach(key => {
                if (!files[key]) delete files[key];
            });
            
            // Show upload progress
            const submitButton = e.target.querySelector('button[type="submit"]');
            submitButton.dataset.originalText = submitButton.textContent;
            Loading.button(submitButton, true);
            submitButton.textContent = 'Uploading files...';
            
            try {
                // Submit application with files
                const result = await ApplicationSystem.submitApplication(applicationData, files);
                
                if (result.success) {
                    toast.success('Thank you! We\'ve received your details. You\'ll be listed on the platform soon, and notified when someone requests your help.');
                    window.closeHelperApplication();
                } else {
                    throw new Error(result.error);
                }
            } finally {
                Loading.button(submitButton, false);
                if (submitButton.dataset.originalText) {
                    submitButton.textContent = submitButton.dataset.originalText;
                }
            }
        } catch (error) {
            errorHandler.handleError(error, 'APPLICATION_SUBMIT');
        }
    });
    
    // Cal.com URL form handler
    document.getElementById('calUrlForm').addEventListener('submit', async (e) => {
        e.preventDefault();
        
        const urlInput = document.getElementById('calUrlInput');
        if (urlInput && urlInput.value) {
            await window.handleCalUrlSubmit(urlInput.value);
        }
    });
    
    // Close modals when clicking outside
    window.addEventListener('click', function(event) {
        // Note: calUrlModal is excluded to prevent accidental closes during copy/paste
        const modals = ['authModal', 'helperDetailModal', 'bookingModal', 'feedbackModal', 'helperApplicationModal', 'calBookingModal'];
        modals.forEach(modalId => {
            const modal = document.getElementById(modalId);
            if (event.target === modal) {
                modal.style.display = 'none';
                // Special handling for Cal.com modal
                if (modalId === 'calBookingModal') {
                    document.getElementById('calBookingContent').innerHTML = '';
                }
            }
        });
    });
    
    // Navbar scroll effect
    window.addEventListener('scroll', function() {
        const navbar = document.getElementById('navbar');
        if (window.scrollY > 50) {
            navbar.classList.add('scrolled');
        } else {
            navbar.classList.remove('scrolled');
        }
    });
}